import type { CopiedFrom, External } from "./common";
import type { BaseDocument, Ref } from "./data";
import type { Installation } from "./installation";
import type { GeoJSONPoint, GeoJSONPolygon } from "./map";
import type { SceneFactProposal } from "./scene-fact-proposal";
import type { Zone } from "./zone";

/**
 * Provenance of a sensor's current `sceneDescription`.
 */
export enum SceneDescriptionSource {
	Auto = "auto",
	Manual = "manual",
}

/**
 * How complete a generated scene description is.
 *
 * THIS IS A GATE, NOT A LABEL. Only a `Final` description is injected into the
 * analyzer prompt; a `Preliminary` one is stored and shown to operators but
 * never sent to the model. The feature's founding constraint is that a
 * description the analyzer acts on is worse than none when it is wrong, and a
 * single frame is exactly when it is most likely to be wrong — a parked truck
 * reads as permanent, a night frame misses what daylight would show.
 *
 * A camera therefore gets a description twice. The first arrives within a
 * minute of its very first clip, built from that one frame: `Preliminary`,
 * visible in the panel, inert. Roughly a day later the generator runs again
 * over frames spread across the camera's first 24 hours — daylight, glare,
 * dusk, IR night — and replaces it with a `Final` description, which is the
 * point it starts affecting analysis. That transition needs no operator
 * involvement and is the end of the automatic lifecycle.
 *
 * A hand-written description is always `Final`: a person decided it is right.
 */
export enum SceneDescriptionStage {
	/** Generated from a full day of frames, or written by hand. Terminal. */
	Final = "final",
	/** Generated from the camera's first clip alone. Will be replaced. */
	Preliminary = "preliminary",
}

/**
 * Status of a sensor
 */
export enum SensorStatus {
	Active = "active",
	Offline = "offline",
	Warning = "warning",
}

/**
 * Domain parameters for creating a Sensor (repository / service layer).
 * Includes the full `Sensor` field set after omit.
 *
 * For the POST /sensors wire body, use {@link CreateSensorRequest}.
 */
export type CreateSensorParams = Omit<
	Sensor,
	"createdAt" | "id" | "updatedAt"
> & {
	/** Installation ID when creating (always a string) */
	installation: string;
	/** Zone ID when creating (always a string, optional) */
	zone?: string;
};

/**
 * POST /sensors request body (matches backend `createSensorSchema`).
 *
 * Shared by dashboard, Org Control, and internal tools so wire contracts
 * stay aligned. Creating sensors is gated by `org:update` until the
 * upcoming Org Control aka Shadow Realm work owns this work.
 *
 * TODO(Chat with Pete/Joe): Revisit with `location:create` / Org Control ownership
 * (see PERMISSIONS.CreateLocation).
 */
export type CreateSensorRequest = {
	externalId: string;
	geolocation?: GeoJSONPoint;
	installation: string;
	name: string;
	status?: SensorStatus;
	type?: SensorType;
	zone: string;
};

/**
 * The open sampling window for a camera awaiting its FINAL scene description.
 *
 * Deliberately holds no clip references. Clips already live at a deterministic,
 * chronologically-sortable S3 prefix (`raw/<installation>/<zone>/<sensor>/
 * <epochMs>.mp4`), so the generator lists the range it wants when it runs
 * rather than the ingest path recording candidates as they arrive. That kept
 * per-clip writes off the hottest path in the system, and it means the
 * generator reads what is actually in the bucket at generation time — a stored
 * key can dangle, as fisheye clips do once preprocessing moves them.
 *
 * What remains is a timer and a failure bound: `startedAt` says when the 24
 * hours are up, and `attempts` says when to stop trying. At
 * `SCENE_DESCRIPTION_MAX_ATTEMPTS` the window is abandoned in place and becomes
 * a terminal, operator-visible failure rather than an invisible hourly retry
 * against a paid model.
 */
export interface SceneSampling {
	/**
	 * Failed generation attempts against this window. Once it reaches
	 * `SCENE_DESCRIPTION_MAX_ATTEMPTS` the window is abandoned in place.
	 */
	attempts: number;
	/**
	 * When generation was last attempted. Absent until the first attempt, which
	 * is deliberate: Mongo sorts missing values first ascending, so never-tried
	 * cameras lead the queue and repeat failures drift to the back.
	 */
	lastAttemptAt?: Date;
	/** Arrival of the camera's first clip — when the 24-hour clock started. */
	startedAt: Date;
}

/**
 * Sensor represents an individual sensor device
 * A Sensor belongs to an Installation (required) and optionally to a Zone
 */
export interface Sensor extends BaseDocument {
	/** Provenance, set only on sensors created by the cross-tenant copy tool */
	copiedFrom?: CopiedFrom;
	/** External identifier from the source system (e.g., Lookout) */
	external: External;
	/** Optional geographic coordinates of the sensor as GeoJSON Point */
	geolocation?: GeoJSONPoint | null;
	/** Reference to the Installation this sensor belongs to (required) */
	installation: Ref<Installation>;
	/** Timestamp of the last input received from this sensor (e.g., video upload) */
	lastInputReceived?: Date;
	/** Name of the sensor (required) */
	name: string;
	/**
	 * Auto-generated, physical-only description of what this camera permanently
	 * sees. Injected into the analyzer prompt as background context so fixed
	 * scenery (floor patterns, bollards, fixtures) is not misread as a hazard.
	 * Never a list of things to watch for — see `SCENE_DESCRIPTION_PROMPT`.
	 */
	sceneDescription?: string;
	/**
	 * Why the most recent generation attempt failed, for operator diagnostics.
	 * Cleared whenever a description is stored. Written by all three triggers —
	 * first-clip, the 24-hour pass, and regenerate — so the panel has one place
	 * to look regardless of which one ran.
	 */
	sceneDescriptionError?: string;
	/** When `sceneDescription` was last written. */
	sceneDescriptionGeneratedAt?: Date;
	/** Whether the current description came from the generator or a human edit. */
	sceneDescriptionSource?: SceneDescriptionSource;
	/** Whether this is the first-clip draft or the full-day replacement. */
	sceneDescriptionStage?: SceneDescriptionStage;
	/**
	 * Human-taught, physical-only layout facts: what lies beyond each frame
	 * edge, where the entrances and exits are and where they lead. Drafted from
	 * a staff note or a customer's edit and applied by staff through a
	 * `SceneFactProposal`. A separate field so regenerating the description
	 * never erases what a person taught; always treated as final. Appended last
	 * to the analyzer's scene reference. Descriptive only, never a directive.
	 */
	sceneFacts?: string;
	/** The proposal revision that produced the current `sceneFacts`. */
	sceneFactsProposal?: Ref<SceneFactProposal>;
	/** When `sceneFacts` was last written (including cleared by a revert). */
	sceneFactsUpdatedAt?: Date;
	/** S3 keys of the frames the description was generated from (audit trail). */
	sceneFrameKeys?: string[];
	/**
	 * Camera geometry paragraph: mounting height, tilt, lens, near versus far,
	 * apparent size of a person near versus far. A property of the camera, not
	 * the scene, so it is generated from the first clip and injected right
	 * away, after `sceneDescription`, whatever that description's stage.
	 */
	sceneGeometry?: string;
	/** When geometry was last attempted, for the backfill's retry backoff. */
	sceneGeometryAttemptedAt?: Date;
	/** Every draft the generator produced; `sceneGeometry` is the one kept. */
	sceneGeometryDrafts?: string[];
	/** Why the most recent geometry attempt stored nothing. */
	sceneGeometryError?: string;
	sceneGeometryGeneratedAt?: Date;
	sceneGeometrySource?: SceneDescriptionSource;
	/**
	 * Open sampling window for the final description. Present between the
	 * camera's first clip and the 24-hour pass; cleared once that pass stores a
	 * final description. A preliminary description does NOT clear it.
	 */
	sceneSampling?: SceneSampling;
	/** Current status of the sensor (required) */
	status: SensorStatus;
	/** Type of sensor, e.g., "camera", "motion" (required) */
	type: SensorType;
	/**
	 * The video sensor this metadata sensor's stream narrates, when paired
	 * manually in the cloud. Takes precedence over the edge-declared
	 * `videoSensorId` on the ingest envelope. Absent means no manual pairing.
	 */
	videoSensor?: Ref<Sensor>;
	/** Optional GeoJSON Polygon representing what the sensor can see */
	viewingArea?: GeoJSONPolygon;
	/** Optional reference to a Zone */
	zone?: Ref<Zone>;
}

/**
 * Live health fields for one sensor. Polled by the dashboard map to keep
 * pin status current without downloading full sensor documents.
 */
export type SensorLiveStatus = Pick<
	Sensor,
	"id" | "lastInputReceived" | "status"
>;

/**
 * Aggregated sensor status summary
 * Used for dashboard display of sensor health at a glance
 */
export interface SensorSummary {
	/** Count of sensors in offline state */
	offline: number;
	/** Count of sensors in active state */
	online: number;
	/** Total count of all sensors */
	total: number;
	/** Count of sensors in warning state */
	warning: number;
}

/**
 * Type of sensor (extensible with additional string values)
 */
export type SensorType = "camera" | "motion" | string;

/**
 * PUT /sensors/{sensorId} request body (matches backend `updateSensorSchema`).
 *
 * Shared by dashboard, Shadow Realm, Org Control, SensorService, and handlers.
 * Structure edits stay on this wire until Shadow map cutover; then narrow the
 * customer route to name-only and keep full structure on `/shadow/sensors`.
 *
 * `geolocation: null` clears map coordinates (`$unset` in SensorService).
 */
export type UpdateSensorRequest = {
	geolocation?: GeoJSONPoint | null;
	name?: string;
	status?: SensorStatus;
	type?: SensorType;
	/**
	 * Manual pairing: id of the video sensor this metadata sensor's stream
	 * narrates. `null` clears an existing pairing.
	 */
	videoSensor?: null | string;
	zone?: string;
};
